import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { Users, Settings, Coins, UserPlus, CreditCard, Network, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle, Save, RefreshCw, Gift, Eye } from 'lucide-react';
import type { ReferralConfig } from '../services/ReferralService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type AdminTab = 'config' | 'referrals' | 'rules';

export const AdminReferrals: React.FC = () => {
  const { user } = useUser();
  const { referralService } = useData();

  const [activeTab, setActiveTab] = useState<AdminTab>('config');
  const [config, setConfig] = useState<ReferralConfig[]>([]);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [adminStats, setAdminStats] = useState<any>(null);
  const [allReferrals, setAllReferrals] = useState<any[]>([]);
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewRules, setPreviewRules] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configData, statsData, referralsData] = await Promise.all([
        referralService.getReferralConfig(),
        referralService.getAdminReferralStats(),
        referralService.getAllReferrals({ limit: 50, offset: 0 }),
      ]);
      setConfig(configData);
      setAdminStats(statsData);
      setAllReferrals(referralsData.data);
      setReferralCount(referralsData.count);

      const values: Record<string, any> = {};
      configData.forEach((c) => {
        values[c.key] = c.value;
      });
      setEditValues(values);
    } catch (err) {
      console.error('Error loading admin referral data:', err);
      setError('Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setSaveSuccess(false);
    setError(null);

    try {
      for (const c of config) {
        if (editValues[c.key] !== c.value) {
          await referralService.updateReferralConfig(c.key, editValues[c.key], user.id);
        }
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      await loadData();
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const updateValue = (key: string, val: any) => {
    setEditValues((prev) => ({ ...prev, [key]: val }));
  };

  const isNumericKey = (key: string) =>
    key.includes('tokens_level') || key === 'max_direct_referrals' || key === 'max_depth' || key === 'referral_expiry_days';

  const isBooleanKey = (key: string) => key === 'program_enabled';

  const isTextKey = (key: string) => key === 'rules_text';

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Referral Program</h1>
          <p className="text-white/50 text-sm">Configure rewards, rules, and view referral activity.</p>
        </div>
        {activeTab === 'config' && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium"
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : saveSuccess ? (
              <CheckCircle size={16} />
            ) : (
              <Save size={16} />
            )}
            {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={18} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <MiniStat label="Total Referrals" value={adminStats?.totalReferrals || 0} icon={<Users size={16} />} />
        <MiniStat label="Completed" value={adminStats?.completedReferrals || 0} icon={<CheckCircle size={16} />} />
        <MiniStat label="Pending" value={adminStats?.pendingReferrals || 0} icon={<RefreshCw size={16} />} />
        <MiniStat label="Signup Tokens" value={Math.round(adminStats?.totalSignupTokensAwarded || 0)} icon={<Gift size={16} />} />
        <MiniStat label="Sub Bonuses" value={Math.round(adminStats?.totalSubscriptionBonusesAwarded || 0)} icon={<CreditCard size={16} />} />
        <MiniStat label="Active Referrers" value={adminStats?.usersWithReferrals || 0} icon={<Network size={16} />} />
      </div>

      <div className="flex gap-2 mb-6">
        {(['config', 'referrals', 'rules'] as AdminTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/20'
                : 'bg-kod-purple-700 text-white/60 hover:text-white'
            }`}
          >
            {tab === 'config' && 'Configuration'}
            {tab === 'referrals' && `Activity (${referralCount})`}
            {tab === 'rules' && 'Rules Editor'}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <ConfigTab
          config={config}
          editValues={editValues}
          updateValue={updateValue}
          isNumericKey={isNumericKey}
          isBooleanKey={isBooleanKey}
          isTextKey={isTextKey}
        />
      )}

      {activeTab === 'referrals' && (
        <ReferralsTab referrals={allReferrals} />
      )}

      {activeTab === 'rules' && (
        <RulesTab
          value={editValues['rules_text'] || ''}
          onChange={(v) => updateValue('rules_text', v)}
          preview={previewRules}
          togglePreview={() => setPreviewRules(!previewRules)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
};

const MiniStat: React.FC<{ label: string; value: number | string; icon: React.ReactNode }> = ({
  label,
  value,
  icon,
}) => (
  <div className="card !p-3">
    <div className="flex items-center gap-1.5 mb-1 text-white/40">{icon}<span className="text-xs">{label}</span></div>
    <p className="text-lg font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
  </div>
);

const ConfigTab: React.FC<{
  config: ReferralConfig[];
  editValues: Record<string, any>;
  updateValue: (key: string, val: any) => void;
  isNumericKey: (key: string) => boolean;
  isBooleanKey: (key: string) => boolean;
  isTextKey: (key: string) => boolean;
}> = ({ config, editValues, updateValue, isNumericKey, isBooleanKey, isTextKey }) => {
  const numericConfigs = config.filter((c) => isNumericKey(c.key));
  const booleanConfigs = config.filter((c) => isBooleanKey(c.key));

  const programSettings = numericConfigs.filter(
    (c) => c.key === 'max_direct_referrals' || c.key === 'max_depth' || c.key === 'referral_expiry_days'
  );

  const signupTokens = numericConfigs.filter((c) => c.key.startsWith('signup_tokens_level'));
  const subTokens = numericConfigs.filter((c) => c.key.startsWith('subscription_tokens_level'));

  return (
    <div className="space-y-6">
      <div className="card">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Settings size={18} className="text-teal-400" />
          Program Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {booleanConfigs.map((c) => (
            <div key={c.key}>
              <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                {c.label}
              </label>
              <button
                onClick={() => updateValue(c.key, !editValues[c.key])}
                className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-all border ${
                  editValues[c.key]
                    ? 'bg-teal-600/20 border-teal-500/30 text-teal-400'
                    : 'bg-red-500/10 border-red-500/20 text-red-400'
                }`}
              >
                {editValues[c.key] ? 'Enabled' : 'Disabled'}
              </button>
              <p className="text-xs text-white/30 mt-1">{c.description}</p>
            </div>
          ))}
          {programSettings.map((c) => (
            <div key={c.key}>
              <label className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                {c.label}
              </label>
              <input
                type="number"
                value={editValues[c.key] ?? ''}
                onChange={(e) => updateValue(c.key, parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-white/[0.05] border border-white/[0.08] text-white rounded-lg focus:outline-none focus:border-teal-500/40 text-sm"
                min={1}
              />
              <p className="text-xs text-white/30 mt-1">{c.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <UserPlus size={18} className="text-teal-400" />
            Signup Token Rewards
          </h3>
          <div className="space-y-3">
            {signupTokens
              .sort((a, b) => a.key.localeCompare(b.key))
              .map((c) => {
                const level = c.key.replace('signup_tokens_level_', '');
                return (
                  <div key={c.key} className="flex items-center gap-4">
                    <span className="text-sm text-white/60 w-20 shrink-0">Level {level}</span>
                    <input
                      type="number"
                      value={editValues[c.key] ?? ''}
                      onChange={(e) => updateValue(c.key, parseInt(e.target.value) || 0)}
                      className="flex-1 px-3 py-2 bg-white/[0.05] border border-white/[0.08] text-white rounded-lg focus:outline-none focus:border-teal-500/40 text-sm"
                      min={0}
                    />
                    <span className="text-xs text-white/30 shrink-0">tokens</span>
                  </div>
                );
              })}
          </div>
          <p className="text-xs text-white/30 mt-3">
            Awarded immediately when a referred user creates their account.
          </p>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <CreditCard size={18} className="text-amber-400" />
            Subscription Bonus Rewards
          </h3>
          <div className="space-y-3">
            {subTokens
              .sort((a, b) => a.key.localeCompare(b.key))
              .map((c) => {
                const level = c.key.replace('subscription_tokens_level_', '');
                return (
                  <div key={c.key} className="flex items-center gap-4">
                    <span className="text-sm text-white/60 w-20 shrink-0">Level {level}</span>
                    <input
                      type="number"
                      value={editValues[c.key] ?? ''}
                      onChange={(e) => updateValue(c.key, parseInt(e.target.value) || 0)}
                      className="flex-1 px-3 py-2 bg-white/[0.05] border border-white/[0.08] text-white rounded-lg focus:outline-none focus:border-teal-500/40 text-sm"
                      min={0}
                    />
                    <span className="text-xs text-white/30 shrink-0">tokens</span>
                  </div>
                );
              })}
          </div>
          <p className="text-xs text-white/30 mt-3">
            One-time bonus when a referral subscribes to any paid plan.
          </p>
        </div>
      </div>
    </div>
  );
};

const ReferralsTab: React.FC<{ referrals: any[] }> = ({ referrals }) => {
  if (referrals.length === 0) {
    return (
      <div className="card text-center py-16">
        <Users size={40} className="mx-auto mb-4 text-white/20" />
        <p className="text-white/50">No referral activity yet.</p>
      </div>
    );
  }

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06]">
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Referrer</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Referred</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Level</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Status</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Signup</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Sub Bonus</th>
            <th className="text-left py-3 px-3 text-xs font-medium text-white/40 uppercase">Date</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r) => (
            <tr key={r.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
              <td className="py-3 px-3 text-white/80">
                {r.referrer?.full_name || r.referrer?.username || 'Unknown'}
              </td>
              <td className="py-3 px-3 text-white/80">
                {r.referred?.full_name || r.referred?.username || 'Pending'}
              </td>
              <td className="py-3 px-3">
                <span className="text-xs px-2 py-0.5 rounded bg-white/[0.06]">{r.depth}</span>
              </td>
              <td className="py-3 px-3">
                <StatusBadge status={r.status} />
              </td>
              <td className="py-3 px-3">
                {r.signup_tokens_awarded ? (
                  <CheckCircle size={14} className="text-green-400" />
                ) : (
                  <span className="text-white/20">-</span>
                )}
              </td>
              <td className="py-3 px-3">
                {r.subscription_tokens_awarded ? (
                  <CheckCircle size={14} className="text-amber-400" />
                ) : (
                  <span className="text-white/20">-</span>
                )}
              </td>
              <td className="py-3 px-3 text-white/40 text-xs">
                {r.completed_at
                  ? new Date(r.completed_at).toLocaleDateString()
                  : new Date(r.invited_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    completed: 'bg-green-500/10 text-green-400 border-green-500/20',
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    expired: 'bg-red-500/10 text-red-400 border-red-500/20',
    reclaimed: 'bg-white/5 text-white/40 border-white/10',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

const RulesTab: React.FC<{
  value: string;
  onChange: (val: string) => void;
  preview: boolean;
  togglePreview: () => void;
  onSave: () => void;
  saving: boolean;
}> = ({ value, onChange, preview, togglePreview, onSave, saving }) => (
  <div className="card">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold">Referral Rules & Explanation</h3>
      <div className="flex items-center gap-2">
        <button
          onClick={togglePreview}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.06] hover:bg-white/[0.1] text-white/60 hover:text-white rounded-lg transition-colors text-xs font-medium border border-white/[0.08]"
        >
          <Eye size={14} />
          {preview ? 'Edit' : 'Preview'}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white rounded-lg transition-colors text-xs font-medium"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </div>
    </div>
    <p className="text-xs text-white/40 mb-4">
      This markdown text is displayed to users on the referral page and explains the program rules. Edit it to match your current configuration.
    </p>

    {preview ? (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-6">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h3: ({ children }) => <h3 className="text-base font-semibold text-white mt-6 mb-3 first:mt-0">{children}</h3>,
            h2: ({ children }) => <h2 className="text-lg font-bold text-white mt-6 mb-3 first:mt-0">{children}</h2>,
            p: ({ children }) => <p className="text-sm text-white/70 mb-3 leading-relaxed">{children}</p>,
            ol: ({ children }) => <ol className="space-y-2.5 mb-4 pl-0 list-none">{children}</ol>,
            ul: ({ children }) => <ul className="space-y-1.5 mb-4 pl-5 list-disc marker:text-white/30">{children}</ul>,
            li: ({ children, ...props }) => {
              const ordered = (props as any).ordered;
              const index = (props as any).index;
              if (ordered) {
                return (
                  <li className="flex items-start gap-3 text-sm text-white/70 leading-relaxed">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500/15 text-teal-400 text-xs font-bold flex items-center justify-center mt-0.5">
                      {(index ?? 0) + 1}
                    </span>
                    <span className="flex-1">{children}</span>
                  </li>
                );
              }
              return <li className="text-sm text-white/70 leading-relaxed">{children}</li>;
            },
            strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
            table: ({ children }) => (
              <div className="my-4 rounded-lg border border-white/[0.08] overflow-hidden">
                <table className="w-full text-sm">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-white/[0.04]">{children}</thead>,
            tbody: ({ children }) => <tbody className="divide-y divide-white/[0.06]">{children}</tbody>,
            tr: ({ children }) => <tr>{children}</tr>,
            th: ({ children }) => <th className="px-4 py-2.5 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">{children}</th>,
            td: ({ children }) => <td className="px-4 py-2.5 text-sm text-white/60">{children}</td>,
          }}
        >{value}</ReactMarkdown>
      </div>
    ) : (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-96 px-4 py-3 bg-white/[0.05] border border-white/[0.08] text-white rounded-lg focus:outline-none focus:border-teal-500/40 text-sm font-mono resize-y"
        placeholder="Enter markdown rules text..."
      />
    )}
  </div>
);
